import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AuthService} from "../../../shared/services/auth.service";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../shared/ngrx/reducers";
import {FilterService} from "../../../shared/services/filter.service";
import {ActivatedRoute, Router} from "@angular/router";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {NgForm} from "@angular/forms";
import {interval, ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {
  IMPORT_STATUS_IN_PROGRESS,
  PAYMENT_STATUS_COMPLETED
} from "../../../shared/services/dict.service";
import {ChronieService} from "../../../shared/services/chronie.service";
import {delay, distinctUntilChanged, finalize, takeUntil, tap} from "rxjs/operators";
import {CountedAndPaginated} from "../../../shared/interface/response/counted-and-paginated";
import {ChroniePaymentListTypes} from "../../../shared/interface/chronie";
import {getCurrentRouteState} from "../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../shared/ngrx/route-serializer";
import {Sort} from "@angular/material/sort";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {
  IssueInsurancePolicyChronieDialogComponent
} from "../../../shared/components/issue-insurance-policy-chronie-dialog/issue-insurance-policy-chronie-dialog.component";

@Component({
  selector: 'app-chronie-payment-list',
  templateUrl: './chronie-payment-list.component.html',
  styleUrls: ['./chronie-payment-list.component.scss']
})
export class ChroniePaymentListComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() embeddedView = false;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;

  initialFilters: any;
  userPermissions: ReplaySubject<UserPermissions>;
  paymentList: ChroniePaymentListTypes[] = [];
  displayedColumns: string[] = [
    'insuringPartyName', 'createdAt', 'policyNumber', 'agentCode', 'party', 'inkaso', 'status', 'policyDownload', 'actions'
  ];
  totalData = 0;
  sortBy = 'createdAt';
  sortOrder = 'DESC';
  isSending = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);


  // eslint-disable-next-line @typescript-eslint/member-ordering
  readonly IMPORT_STATUS_IN_PROGRESS = IMPORT_STATUS_IN_PROGRESS;
  // eslint-disable-next-line @typescript-eslint/member-ordering
  readonly PAYMENT_STATUS_COMPLETED = PAYMENT_STATUS_COMPLETED;

  constructor(
    private chronieService: ChronieService,
    private auth: AuthService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private store: Store<StoreRootState>,
    private filterService: FilterService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.chronieService.chroniePaymentListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<ChroniePaymentListTypes>) => {
          this.paymentList = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  ngOnInit(): void {
    interval(5000)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.paginator.pageIndex === 0) {
          this.loadListData();
        }
      });
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      policyNumber: 'string',
      insuringParty: 'string',
      ofwca: 'string',
      partner: 'string',
      startCreatedAt: 'string',
      endCreatedAt: 'string',
      agentCode: 'string',
      status: 'string'
    }, filters);
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.sortBy = state.params?.sortBy ? state.params.sortBy : 'createdAt';
        this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'DESC';
        this.loadListData();
      })
    ;
  }


  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;

    if (!this.embeddedView) {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        sortBy: $event.active,
        sortOrder: $event.direction,
        page: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value
      })]);
    } else {
      this.loadListData();
    }
  }

  buildFilters(): any {
    return {...this.initialFilters};
  }

  loadListData(): void {
    this.chronieService.getPaymentLst(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters(),
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      }
    );
  }

  downloadReport(): void {
    this.chronieService.downloadReport(this.initialFilters,
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      })
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  downloadFile(url: string): void {
    this.chronieService.downloadFile(url).subscribe({
      error: (e) => console.error('Błąd pobierania', e),
    });
  }

  sendPolicy(paymentId: string, firstName: string, lastName: string, email: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz wysłać maila do <br>' +
          '<span class="font-bold">' + firstName + ' ' +
          lastName + '</span><span class="font-small"> (' + email + ') </span>'
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isSending = true;
        this.chronieService.sendPolicy(paymentId)
          .pipe(
            finalize(() => this.isSending = false)
          ).subscribe({
          next: (res) => {
            this.snack.open('Polisy wysłane do klienta ✅', 'OK', {duration: 3000});

          },
          error: (err: HttpErrorResponse) => {
            const {message, action} = this.mapPolicySendError(err);
            this.snack.open(message, action, {duration: 3000});
          }
        });
      }
    });

  }


  sendPayU(policyUuid: string, periodStart: string): void {
    const dateOnly = (periodStart || '').substring(0, 10);

    const dialogRef = this.dialog.open(IssueInsurancePolicyChronieDialogComponent, {
      width: '500px',
      data: {
        text: 'Wystaw polisę',
        body: 'Data rozpoczęcia ochrony na polisie: ' + dateOnly
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!result || result.success === false) {
        this.snack.open('Anulowano wystawienie polisy', '', { duration: 2000 });
        return;
      }

      // success === true
      this.isSending = true;
      const selectedDate: string = (result.data || '').substring(0, 10);

      this.chronieService.issuePolicy(policyUuid, selectedDate)
        .pipe(
          finalize(() => this.isSending = false)
        ).subscribe({
        next: (res) => {
          this.snack.open('Polisy została wystawiona i wysłana do klienta ✅', 'OK', {duration: 3000});

        },
        error: (err: HttpErrorResponse) => {
          const {message, action} = this.mapPolicySendError(err);
          this.snack.open(message, action, {duration: 3000});
        }
      });
    });
  }


  private mapPolicySendError(error: HttpErrorResponse): { message: string; action?: string } {
    const reason = (error?.error && typeof error.error === 'object') ? error.error.reason : undefined;
    const backendMsg = (error?.error && typeof error.error === 'object') ? error.error.error ?? error.error.message : undefined;

    // Najpierw „znane” przypadki z Twojego backendu:
    if (error.status === 400) {
      return {message: 'Niepoprawne dane żądania (400). Sprawdź paymentId / Polisa nie została opłacona!.'};
    }

    if (error.status === 502 && reason === 'email_delivery_failed') {
      return {message: 'Błąd zewnętrznego serwera e-mail (502).', action: 'Ponów'};
    }

    if (error.status === 502 && reason === 'external_issue_failed') {
      return {message: 'Nie udało się wystawić/pozyskać polis (Chronie, 502).', action: 'Ponów'};
    }

    if (error.status === 503) {
      return {message: 'Usługa tymczasowo niedostępna (503). Spróbuj ponownie za chwilę.', action: 'Ponów'};
    }

    // Fallback – pokaż cokolwiek wiemy
    const generic = backendMsg ? ` Błąd: ${backendMsg}` : '';
    const code = error.status ? ` (${error.status})` : '';
    return {message: `Przy wysyłaniu polis wystąpił błąd${code}.${generic}`};
  }


}
